import React, { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { useAsyncValue, useLocation, useNavigate, useParams, useRevalidator } from 'react-router-dom';
import { format } from 'date-fns';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import debug from 'debug';

//Components
import ActionContentCardResume from '../../../../../components/display/ActionContentCardResume';

// Types
import { WorkOrder } from '../../../../../types/workOrder';
import { LoaderType } from './types';
import { OrderType, OrderState } from '../../../../../types/orders';

// Redux
import {
  roidOTSelected,
  roidOTSelectedReset,
} from '../../../../../state/actions/ui/roidOTSelectedGeneralView';
import { orderCloseSetPending, orderCloseRemoveDeclined } from '../../../../../state/actions/ui/orderCloseConfirmation';
import { removeNewActionStorage } from '../../../../../utils/resetData';
import { actionTabsUnlockAll } from '../../../../../state/actions/ui/actionTabs';
import { readCreationDraft } from '../../../../../utils/draftScopes';
import { creationSummaryEditPath } from '../creationDraft';
import {
  reconcileValidationActionContext,
  releaseForeignValidationPointer,
} from '../../../../../utils/validationActionContext';
import { useRevalidateOnSync } from '../../../../../offline/useRevalidateOnSync';
import { modalIds } from '../../../../../components/modals/SuperModal/types';
import { openModal, closeModal } from '../../../../../state/actions/ui/modal';

// Auth
import { useAuth } from '../../../../../hooks/useAuth';
import { Role } from '../../../../../types/auth';

// Mutations
// C-11.1: UPDATE_ORDER + UPDATE_ACTION eliminados.
// SAVE_ACTION sustituye a UPDATE_ACTION (path homogéneo con ActionsValidation,
// atómico, mismo helper de cache para sync offline).
import { SAVE_ACTION } from '../../../../../gql/mutations/actions';
import { prependActionToCache, evictDerivedCaches } from '../../../../../gql/cacheHelpers';
// T99: CLOSE_ORDER import removed - closing happens in ShowOT/ActionsValidation

// Queries
import { GET_ORDER_BY_ID } from '../../../../../gql/queries/orders/orders';

// Apollo Client
import { client } from '../../../../../gql/client';
import { GET_SURVEILLANCE_ROUTE_CARD_BY_ORDER_ID } from '../../../../../gql/queries/surveillanceRoute';

// Toasts
import { showSuccessToast } from '../../../../../utils/toasts';

// T99: Close Order Confirmation Modal - Ahora se usa via SuperModal/Redux

// Validation
import { LocationSchema } from '../../../../../components/forms/ActionForms/LocationForm/schema';
import { createOperatorsAndVehicleSchema } from '../../../../../components/forms/ActionForms/OperatorsAndVehicles/schema';
import { EditingActionSchema } from '../../../../../components/forms/ActionForms/ActionEdition/schema';
import { basicScheduleContextFromSession } from '../../../../../components/forms/ActionForms/ActionEdition/basicSchedule';

// Hooks
import { useFeatureFlagWithLoading } from '../../../../../hooks/useFeatureFlag';
import { decompressSessionStorage } from '../../../../../utils/sessionStorage';

// Permissions
import {
  BLOCKED_STATES_FOR_CONTENT,
  TABLET_CONTENT_CREATION_STATES,
  canRoleChangeStateOf,
  roleCanViewBudgets,
} from '../../../../../utils/rolePermissions';
import { formActionNaming, formActionValidationNaming } from '../../../../../components/forms/ActionForms/constants';

// Styled components for confirmation modal
import styled from 'styled-components';
import { Typography } from '../../../../../components/display/Typography';
import { MainButton } from '../../../../../components/inputs/buttons/MainButton';
import { withNavigationFeedback } from '../../../../../utils/navigationFeedback';
import { isInternalPath } from '../../../../../utils/internalPath';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  padding: 2rem;
  border-radius: 8px;
  max-width: 400px;
  width: 90%;
  text-align: center;
`;

const ModalButtons = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: center;
  margin-top: 1.5rem;
`;

const ErrorList = styled.ul`
  list-style: none;
  padding: 0;
  margin-top: 1rem;
  max-height: 400px;
  overflow-y: auto;
`;

const ErrorGroup = styled.li`
  margin-bottom: 1.5rem;
`;

const TabName = styled.div`
  font-weight: 600;
  color: #d32f2f;
  margin-bottom: 0.5rem;
  cursor: pointer;
  &:hover {
    text-decoration: underline;
  }
`;

const ErrorItem = styled.div`
  padding: 0.5rem;
  background: #ffebee;
  border-radius: 4px;
  margin-bottom: 0.5rem;
  font-size: 0.9rem;
`;

// Roles de las rutas `actions-validation/:id` del router (OT/global, campaña y
// recorrido). Si se cambian allí, aquí también.
const ACTION_VALIDATION_EDIT_ROLES: readonly string[] = [
  Role.SuperAdmin,
  Role.Coex,
  Role.Capataz,
  Role.RoomTeam,
  Role.Tablet,
];

// Debug logger for parent order auto-close
const logAutoClose = debug('rois:action-summary:auto-close');

// Debug logger for context-aware redirects
const logRedirect = debug('rois:action-summary:redirect');

// Enable debug logs in development
if (import.meta.env.DEV) {
  debug.enable('rois:action-summary:*');
}

export default function ActionSummaryPage(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  // Id de la actuación de ESTA pantalla: el de la URL. Es la única referencia
  // fiable — el contexto de la pestaña puede ser de otra (diagnóstico
  // 2026-10-08 § 8).
  const { id: urlActionId } = useParams();
  const dispatch = useDispatch();
  const { currentRole } = useAuth();
  const revalidator = useRevalidator();

  // 🔴 Refrescar cuando la cola de fotos termina de subir.
  //
  // Este resumen NO lee de la caché de Apollo: pinta el snapshot que le dio el
  // loader de react-router. Por eso actualizar la caché no basta — hay que
  // revalidar el loader. Sin esto, al terminar de subir 31 fotos el resumen
  // seguía enseñando las 3 que había al abrirlo y solo se veían recargando la
  // página a mano (visto en vivo el 2026-08-10).
  //
  // El componente sirve TODAS las rutas de resumen de actuación, incluida la
  // de `actions-validation`, así que con ponerlo aquí quedan cubiertas todas.
  // El hermano de accidentes (`AccidentPageSummary`) ya lo tenía.
  useRevalidateOnSync();

  // Context-aware navigation: Read origin from session storage
  // Check both validation and creation session storage keys
  // ¿Resumen de VALIDACIÓN? Este componente pinta también el de CREACIÓN.
  const isValidationSummary = location.pathname
    .split('/')
    .includes('actions-validation');
  const sessionData = useMemo(() => {
    // First, try the validation session storage key — solo si es de la
    // actuación de la URL; si es de otra, se descarta entero. En el resumen
    // de CREACIÓN solo se quita el puntero ajeno: no se reclama el ámbito de
    // validación (Ruling 17, mismo criterio que el loader).
    const ownContext = isValidationSummary
      ? reconcileValidationActionContext(urlActionId)
      : releaseForeignValidationPointer(urlActionId);
    let raw = ownContext
      ? sessionStorage.getItem(formActionNaming.NEW_VALIDATION_ACTION_DATA)
      : null;
    let sourceKey = formActionNaming.NEW_VALIDATION_ACTION_DATA;

    console.log('🔑 [ActionSummary] Checking VALIDATION session storage:', {
      key: formActionNaming.NEW_VALIDATION_ACTION_DATA,
      hasRawData: !!raw,
      rawLength: raw?.length || 0,
    });

    // If validation key is empty, try the action creation key (fallback for creation flow)
    if (!raw) {
      raw = sessionStorage.getItem(formActionNaming.NEW_ACTION_DATA);
      sourceKey = formActionNaming.NEW_ACTION_DATA;
      console.log('🔑 [ActionSummary] Checking CREATION session storage:', {
        key: formActionNaming.NEW_ACTION_DATA,
        hasRawData: !!raw,
        rawLength: raw?.length || 0,
      });
    }

    logRedirect('📦 Reading session storage:', {
      sourceKey,
      hasRawData: !!raw,
      rawLength: raw?.length || 0,
    });

    if (!raw) {
      console.log('⚠️ [ActionSummary] No session data found in either key - will use defaults');
      logRedirect('⚠️ No session data found - will use defaults');
      return null;
    }

    try {
      const parsed = JSON.parse(decompressSessionStorage(raw));

      // El puntero del asistente de CREACIÓN solo vale como origen si es de
      // esta actuación: si ya apunta a otra, es de un asistente anterior.
      if (
        sourceKey === formActionNaming.NEW_ACTION_DATA &&
        parsed?.actionId &&
        urlActionId &&
        parsed.actionId !== urlActionId
      ) {
        return null;
      }

      console.log('✅ [ActionSummary] Session data parsed from', sourceKey, ':', parsed);
      logRedirect('✅ Session data parsed successfully:', parsed);

      // Normalize field names: action creation uses 'orderParentId', validation uses 'parentOrderId'
      // Map to consistent 'parentOrderId' for navigation logic
      if (parsed.orderParentId && !parsed.parentOrderId) {
        parsed.parentOrderId = parsed.orderParentId;
        parsed.origin = 'workorder'; // Actions created from ShowOT always originate from workorder
        console.log('🔄 [ActionSummary] Normalized orderParentId → parentOrderId:', parsed.parentOrderId);
      }

      return parsed;
    } catch (error) {
      console.log('❌ [ActionSummary] Error parsing session data:', error);
      logRedirect('❌ Error parsing session data:', error);
      return null;
    }
    // Depende de la actuación de la URL: React reutiliza esta pantalla al
    // pasar de un resumen a otro y con `[]` se quedaba el contexto del primero.
  }, [urlActionId, isValidationSummary]);

  const navigationOrigin = sessionData?.origin || 'validation-table';
  const parentOrderId = sessionData?.parentOrderId;
  const orderParentRoid = sessionData?.orderParentRoid;

  // Detect if parent order is a campaign based on:
  // 1. URL contains "campaign"
  // 2. Parent ROID starts with "Ca" (campaign ROID format)
  const isCampaign = useMemo(() => {
    const urlHasCampaign = location.pathname.includes('/campaign/');
    const roidIndicatesCampaign = orderParentRoid?.startsWith('Ca');
    return urlHasCampaign || roidIndicatesCampaign;
  }, [location.pathname, orderParentRoid]);

  // Log extracted navigation values - using console.log for visibility
  console.log('🧭 [ActionSummary] Navigation context extracted:', {
    navigationOrigin,
    parentOrderId,
    orderParentRoid,
    sessionDataExists: !!sessionData,
    allSessionFields: sessionData ? Object.keys(sessionData) : [],
  });
  logRedirect('🧭 Navigation context:', {
    navigationOrigin,
    parentOrderId,
    orderParentRoid,
    isCampaign,
    sessionDataExists: !!sessionData,
    allSessionFields: sessionData ? Object.keys(sessionData) : [],
  });

  // BUG #21 FIX: Usar schema dinámico para hoursType basado en feature flag
  const { isEnabled: showHoursType } = useFeatureFlagWithLoading('showHoursType', true);

  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  // Tarea #7 — Pendent de certificar (estado administrativo post-validación).
  const [showPendingCertificationModal, setShowPendingCertificationModal] =
    useState(false);
  const [isMarkingPendingCertification, setIsMarkingPendingCertification] =
    useState(false);
  const [validationErrors, setValidationErrors] = useState<Array<{
    tab: string;
    tabLabel: string;
    field: string;
    message: string;
  }>>([]);
  const [showValidationErrorsModal, setShowValidationErrorsModal] = useState(false);

  // T99: Close Order Confirmation - Ahora usa Redux/SuperModal

  // C-11.1: UPDATE_ACTION + UPDATE_ORDER hooks eliminados — el flow validate
  // ahora usa client.mutate(SAVE_ACTION) directamente con cache update vía
  // prependActionToCache. No necesitamos useMutation aquí.
  // T99: closeOrder mutation removed - closing happens in ShowOT/ActionsValidation

  const loaderData = useAsyncValue() as WorkOrder &
    LoaderType & {
      action: LoaderType;
      isActionEdited: string;
      externalOperatorsCount: number;
      orderId?: string;
      state?: string;
      descriptionValidated?: string | null;
      operatorDescription?: string | null;
      creatorName?: string | null;
      registeredByName?: string | null;
    };

  // BUG #10 FIX: Defensive validation - prevent crash when loader returns false/incomplete data
  // Root cause: loadActionSummary returns `false` on error, spreading `false` loses all date fields
  // NOTA: Las actuaciones parciales (state="partial") pueden tener date/registerInitDate/registerEndDate como null
  // Solo bloqueamos si no hay datos de la acción en absoluto (loader falló completamente)
  const loaderFailed = !loaderData || (!loaderData.id && !loaderData.action);

  if (loaderFailed) {
    // Distinguir "no disponible offline" de "no existe / sin permisos" basado en el
    // estado de red en el momento del render. Detalle:
    // docs/superpowers/plans/2026-05-12-offline-edit-action-accident.md § Fase 3.
    const isOffline = typeof navigator !== 'undefined' && !navigator.onLine;
    const titleKey = isOffline
      ? 'validation:errors.actionOfflineNotCached'
      : 'validation:errors.actionNotFound';
    const descKey = isOffline
      ? 'validation:errors.actionOfflineNotCachedDescription'
      : 'validation:errors.actionNotFoundDescription';
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <Typography size="L" variant="semiBold" style={{ color: '#d32f2f' }}>
          {t(titleKey)}
        </Typography>
        <Typography size="M" style={{ marginTop: '0.5rem', color: '#666' }}>
          {t(descKey)}
        </Typography>
        <MainButton
          text={t('buttons:back', 'Volver')}
          onClick={() => navigate(-1)}
          style={{ marginTop: '1rem' }}
        />
      </div>
    );
  }

  const routes = location.pathname.split('/');
  const isActionEditedBoolean = loaderData.isActionEdited === 'true';
  routes?.shift();

  const isActionValidation = routes.some(
    (item) => item === 'actions-validation'
  );

  // Query parent order state to check if blocked (End/Annulled).
  //
  // El id del padre sale del DATO (`parentExpeditionOrderIDs[0]`, que el loader
  // ya trae) y solo cae al de sessionStorage como respaldo. Al revés —como
  // estaba— el botón Validar aparecía o no PARA LA MISMA ACTUACIÓN según por
  // dónde se hubiera entrado: desde la card del recorrido sí se siembra
  // `parentOrderId` y bloqueaba, pero entrando directo (enlace, recarga,
  // Gestión Horaria) llegaba vacío, no se consultaba nada y el botón se
  // pintaba aunque el padre estuviera cerrado. Verificado en beta sobre
  // At.0000254.0001.02: gris en las pestañas, activo en el resumen.
  //
  // Un bloqueo inconsistente es peor que uno estricto: el usuario ve el botón,
  // lo pulsa y el backend lo rechaza.
  const parentOrderIdForBlockCheck =
    loaderData.parentOrderIdFromData ?? parentOrderId;
  const [parentOrderState, setParentOrderState] = useState<OrderState | null>(null);
  useEffect(() => {
    if (!parentOrderIdForBlockCheck) return;
    client
      .query({
        query: GET_ORDER_BY_ID,
        variables: { id: parentOrderIdForBlockCheck },
        fetchPolicy: 'cache-first',
      })
      .then(({ data }) => {
        if (data?.order?.state) {
          setParentOrderState(data.order.state as OrderState);
        }
      })
      .catch(() => { /* parent order state unknown — allow actions */ });
  }, [parentOrderIdForBlockCheck]);

  const isParentBlocked = parentOrderState
    ? BLOCKED_STATES_FOR_CONTENT.includes(parentOrderState)
    : false;


  // Actuació bàsica (hija de un Recorregut de Vigilància): traje reducido.
  // Hereda tripulación y medios del recorrido, y sus horas las cuenta el
  // turno de patrulla, no ella. El discriminador viene del DATO
  // (`containerType`), no del path: la validación vive bajo `/work-orders/`,
  // así que cualquier gating por URL se pierde justo aquí.
  const isBasicActionValidation =
    isActionValidation && !!loaderData.isBasicAction;

  // Con la pestaña Operarios oculta, todo lo que dependa de su formulario
  // sobra: ni pre-validación de schema (§ validateAllForms) ni secciones de
  // operarios/horas/maquinaria en el resumen.
  const skipOperatorsChecks = isBasicActionValidation;

  // Lo que el resumen PINTA de tripulación va SUELTO, por el dato: la básica
  // no la tiene venga de donde venga la pantalla. Espejo de `skipAnimals` de
  // más abajo y del `isBasic` del informe PDF.
  const hideCrewInSummary = !!loaderData.isBasicAction;

  // Fauna (2026-08-11): la básica no la registra, así que su resumen tampoco
  // la pinta — ni la del asistente ni la de la validación. Ojo: SUELTO, no
  // `isBasicActionValidation`, que está condicionado a `isActionValidation` y
  // dejaría fuera precisamente el resumen del asistente de creación, que es
  // donde el operario acaba de terminar la básica.
  //
  // Spec: docs/superpowers/specs/2026-08-11-basica-sin-fauna-design.md
  const skipAnimals = !!loaderData.isBasicAction;

  // Determine if user can validate (not Tablet or RoomTeam, action not already validated/annulled/end, parent not blocked)
  const canValidate =
    isActionValidation &&
    !isParentBlocked &&
    currentRole !== Role.Tablet &&
    currentRole !== Role.RoomTeam &&
    loaderData.state !== OrderState.Partial &&
    loaderData.state !== OrderState.Validated &&
    loaderData.state !== OrderState.Annulled &&
    loaderData.state !== OrderState.End;

  // Tarea #7 — Puede marcar "Pendent de certificar": sólo Coex, Capataz y
  // SuperAdmin, y sólo si el estado actual no es ya pending-certification/terminal.
  const canMarkPendingCertification =
    isActionValidation &&
    !isParentBlocked &&
    (currentRole === Role.Coex ||
      currentRole === Role.Capataz ||
      currentRole === Role.SuperAdmin) &&
    loaderData.state !== OrderState.Partial &&
    loaderData.state !== OrderState.PendingCertification &&
    loaderData.state !== OrderState.Annulled &&
    loaderData.state !== OrderState.End;

  // Order close confirmation: ActionSummary dispatches to Redux when all actions are validated
  // ShowOT/ActionsValidation read from Redux and show the modal there

  /**
   * Validates all form data from sessionStorage using Yup schemas.
   *
   * This function implements centralized validation for all action forms at the final step,
   * executing all existing Yup schemas and collecting errors from all tabs.
   *
   * @returns {Promise<{ valid: boolean, errors: Array }>} Validation result with all errors
   */
  const validateAllForms = async () => {
    const errors: Array<{
      tab: string;
      tabLabel: string;
      field: string;
      message: string;
    }> = [];

    try {
      // 1. Validate OperatorsAndVehicles form
      // NOTA: Los datos se guardan en DOS lugares:
      // - Key principal (comprimida por useCacheFormInSessionStorage)
      // - Key _interno (SIN comprimir, solo cuando cambia de tab)
      // Priorizamos la key principal que siempre tiene los datos actualizados
      const operatorsFormKey = isActionValidation
        ? formActionValidationNaming.NEW_VALIDATION_OPERATORS_AND_VEHICLE_FORM
        : formActionNaming.NEW_OPERATORS_AND_VEHICLE_FORM;

      // Intentar leer de la key principal primero (COMPRIMIDA)
      let operatorsData: any = null;
      const operatorsDataMain = sessionStorage.getItem(operatorsFormKey);
      if (operatorsDataMain) {
        const decompressed = decompressSessionStorage(operatorsDataMain);
        operatorsData = JSON.parse(decompressed);
      } else {
        // Si no existe la key principal, intentar con _interno (SIN COMPRIMIR)
        const operatorsDataInternal = sessionStorage.getItem(`${operatorsFormKey}_interno`);
        if (operatorsDataInternal) {
          operatorsData = JSON.parse(operatorsDataInternal); // SIN decompressSessionStorage
        }
      }

      // Actuació bàsica en validación: la pestaña Operarios está oculta, así
      // que pre-validar su schema solo puede acabar mal — un error aquí manda
      // al usuario a `../:id/operators-and-vehicles`, una pestaña que ya no
      // existe. Bounce contra un muro. La básica hereda la tripulación del
      // recorrido y no tiene nada que validar en ese formulario.
      if (operatorsData && !skipOperatorsChecks) {
        try {
          await createOperatorsAndVehicleSchema(showHoursType).validate(operatorsData, { abortEarly: false });
        } catch (err: any) {
          err.inner?.forEach((error: any) => {
            errors.push({
              tab: 'operators-and-vehicles',
              tabLabel: t('workOrders:tabs.operatorsAndVehicles'),
              field: error.path,
              message: t(error.message),
            });
          });
        }
      }

      // 2. Validate Location form
      const locationFormKey = isActionValidation
        ? formActionValidationNaming.NEW_VALIDATION_LOCATION_FORM
        : formActionNaming.NEW_LOCATION_FORM;
      const locationData = sessionStorage.getItem(locationFormKey);
      if (locationData) {
        const decompressed = decompressSessionStorage(locationData);
        const locationFormData = JSON.parse(decompressed);
        try {
          await LocationSchema.validate(locationFormData, { abortEarly: false });
        } catch (err: any) {
          err.inner?.forEach((error: any) => {
            errors.push({
              tab: 'location',
              tabLabel: t('workOrders:tabs.location'),
              field: error.path,
              message: t(error.message),
            });
          });
        }
      }

      // 3. Validate ActionEdition form
      const editionFormKey = isActionValidation
        ? formActionValidationNaming.NEW_VALIDATION_EDITION_FORM
        : formActionNaming.NEW_EDITION_FORM;
      const editionData = sessionStorage.getItem(editionFormKey);
      if (editionData) {
        const decompressed = decompressSessionStorage(editionData);
        const editionFormData = JSON.parse(decompressed);
        try {
          // Mismo contexto que la validación central y que el formulario: sin
          // él la regla fin ≥ inicio no se aplicaba aquí y «Validar» seguía
          // adelante con el horario al revés (bug 12-oct).
          await EditingActionSchema.validate(editionFormData, {
            abortEarly: false,
            context: basicScheduleContextFromSession(
              isActionValidation ? 'validation' : 'wizard',
              editionFormData
            ),
          });
        } catch (err: any) {
          err.inner?.forEach((error: any) => {
            errors.push({
              tab: 'edition',
              tabLabel: t('workOrders:tabs.actionData'),
              field: error.path,
              message: t(error.message),
            });
          });
        }
      }

      // Note: Materials, Machinery, Animals, and BudgetItems forms don't have Yup schemas
      // They handle their own validation internally

      return {
        valid: errors.length === 0,
        errors,
      };
    } catch (error) {
      console.error('Error validating forms:', error);
      return {
        valid: false,
        errors: [{
          tab: 'general',
          tabLabel: t('validation:errors.general'),
          field: 'general',
          message: t('validation:errors.unexpectedError'),
        }],
      };
    }
  };

  useLayoutEffect(() => {
    if (!loaderData) {
      navigate('..');
    }

    if (loaderData) {
      dispatch(
        roidOTSelected(
          isActionValidation
            ? 'actions-validation'
            : OrderType.Action,
          loaderData.roid!,
          loaderData.id as string
        )
      );
    }
  }, []);

  // ¿Estamos en el asistente DENTRO de un recorrido? Se detecta por la URL
  // (`/surveillance-route/:routeId/new-action/...`), que es lo mismo que ya
  // hace `srRouteIdMatch` más abajo para el retorno. Se declara aquí porque
  // `handleEditAction` lo necesita.
  const isSurveillanceRouteWizard =
    /^\/surveillance-route\/[^/]+\/new-action/.test(window.location.pathname);

  // Resumen de CREACIÓN cuyo borrador en curso es de otra actuación (una
  // puerta empezó otra bajo el mismo padre y se volvió con «atrás»): «Editar»
  // lleva a la edición de ESTA por validación, no al asistente (auditoría de
  // creación 2.3). `null` = el borrador es de esta: al asistente, como siempre.
  const editOfThisActionPath = isActionValidation
    ? null
    : creationSummaryEditPath({
        pathname: location.pathname,
        actionId: (loaderData.id as string | undefined) ?? urlActionId,
        isBasicAction: !!loaderData.isBasicAction,
        draft: readCreationDraft(),
      });
  // Los roles de las rutas `actions-validation/:id` del router (los tres
  // árboles). `OperatingTeam` entra al asistente de crear (OT y campaña) pero
  // no a validar: sin borrador de esta actuación, para él no hay «Editar».
  const canOpenActionValidation =
    !!currentRole && ACTION_VALIDATION_EDIT_ROLES.includes(currentRole);
  const editBlockedForRole = !!editOfThisActionPath && !canOpenActionValidation;

  const handleEditAction = () => {
    // Asistente de creación con el borrador de OTRA actuación (o sin
    // borrador): «Editar» no abre el asistente, que editaría ese borrador, sino
    // la edición de ESTA por validación (`creationSummaryEditPath`).
    if (!isActionValidation && editOfThisActionPath) {
      // Sin acceso a la validación el botón no se pinta (más abajo); esto es
      // solo el cinturón.
      if (!canOpenActionValidation) return;
      // Como la puerta de validación de la tarjeta (`ActionOT`): pestañas
      // libres, que en validación la actuación ya existe entera.
      dispatch(actionTabsUnlockAll());
      navigate(editOfThisActionPath);
      return;
    }
    if (isActionValidation) {
      // «Editar» entra por la primera pestaña. En una básica esa pestaña
      // (Operarios) está oculta, así que la primera editable es Ubicación —
      // sin esto se navegaría a una pestaña que el layout rebota de vuelta.
      navigate(
        `../${loaderData.id}/${skipOperatorsChecks ? 'location' : 'operators-and-vehicles'}`
      );
    } else {
      // Asistente. En el recorrido solo se crean actuaciones BÁSICAS, y su
      // primera pestaña es Ubicación — Operarios está oculta (FR-9.3). Sin
      // esto, `../details` caía en el índice de la ruta y aterrizaba en
      // Operarios y Vehículos, una pantalla que la básica no tiene
      // (fix 2026-08-05). El índice del bloque RV ya rebota a Ubicación; esto
      // es el cinturón, para no depender solo de la redirección.
      navigate(isSurveillanceRouteWizard ? `../details/location` : `../details`);
    }
  };

  // Retorno determinista al origen tras validar/guardar/pendent. Prioriza el
  // returnTo explícito que graba cada punto de entrada; si falta (sesión antigua
  // en caché) cae al cálculo por origin/parentOrderId. Nunca usa navigate(-1)
  // (rebotaba al wizard cuando había historial de edición). Ver spec 2026-06-05.
  // Contexto SR (fix 2026-07-04): la At. basica vuelve a las Órdenes
  // asociadas de su RV, no al detalle de work-orders.
  const srRouteIdMatch = window.location.pathname.match(
    /^\/surveillance-route\/([^/]+)/,
  );

  // Ese `match` mira la URL ACTUAL, así que solo acierta mientras se CREA la
  // actuación dentro del recorrido. Al VALIDARLA la URL es `/work-orders/…` y
  // siempre daba `null` → la básica salía a la tabla de validación en vez de
  // volver a su recorrido. Aquí se deriva del DATO.
  //
  // Ojo con la entidad doble: la ruta `/surveillance-route/:routeId` quiere el
  // id de la ENTIDAD `SurveillanceRoute`, no el de su Order gemela (que es lo
  // que tenemos). Confundirlos da un 404 mudo. La query ligera de tarjeta hace
  // la traducción y comparte caché normalizada con el resto del módulo.
  const [basicActionRouteId, setBasicActionRouteId] = useState<string | null>(
    null,
  );
  useEffect(() => {
    if (!isBasicActionValidation || !loaderData.parentOrderIdFromData) return;
    let cancelled = false;
    client
      .query({
        query: GET_SURVEILLANCE_ROUTE_CARD_BY_ORDER_ID,
        variables: { orderId: loaderData.parentOrderIdFromData },
        fetchPolicy: 'cache-first',
      })
      .then(({ data }) => {
        if (!cancelled && data?.surveillanceRouteByOrderId?.id) {
          setBasicActionRouteId(data.surveillanceRouteByOrderId.id);
        }
      })
      .catch(() => {
        /* sin recorrido resoluble → retorno por defecto, no se rompe nada */
      });
    return () => {
      cancelled = true;
    };
  }, [isBasicActionValidation, loaderData.parentOrderIdFromData]);

  const srExitRoute = srRouteIdMatch
    ? `/surveillance-route/${srRouteIdMatch[1]}/child-orders`
    : basicActionRouteId
      ? `/surveillance-route/${basicActionRouteId}/child-orders`
      : null;

  // Al validar una básica, el destino del recorrido NO puede pisar el origen.
  // Se colaba delante de `returnTo` y mandaba al recorrido vinieras de donde
  // vinieras — desde la tabla de Actuaciones o desde Gestión Horaria también.
  // Una básica se revisa desde las mismas pantallas que una normal, así que
  // vuelve a la que te trajo (reporte usuario 2026-08-05).
  //
  // La salvedad: la card del recorrido graba como `returnTo` el DETALLE DE LA
  // ORDER PADRE (`/work-orders/<id>`), y para una básica ese padre es la Order
  // gemela del recorrido — una pantalla que no es la del módulo RV. Ahí sí
  // manda el recorrido; es el caso que arregló el spec del 2026-08-01.
  const parentOrderExits = parentOrderId
    ? [`/work-orders/${parentOrderId}`, `/work-orders/campaign/${parentOrderId}`]
    : [];
  // El destino llega desde FUERA del componente (el contexto de sesión que
  // graban las tablas y las cards), que es exactamente el caso para el que
  // existe `isInternalPath`: rechaza `//host` y, sobre todo, el `\` que algunos
  // navegadores normalizan a `/` antes de navegar. Hoy todos los que lo
  // escriben usan literales internos, así que esto no cambia ningún flujo —
  // pero era el único consumidor de `returnTo` que se saltaba el saneador, y su
  // propio JSDoc dice que lo usa "todo destino de vuelta que venga de fuera".
  const safeReturnTo = isInternalPath(sessionData?.returnTo)
    ? sessionData.returnTo
    : undefined;
  const returnsToParentDetail =
    !!safeReturnTo && parentOrderExits.includes(safeReturnTo);

  const getExitRoute = (): string => {
    if (srRouteIdMatch) return `/surveillance-route/${srRouteIdMatch[1]}/child-orders`;
    // El origen explícito manda, salvo que apunte al detalle del padre.
    if (safeReturnTo && !returnsToParentDetail) return safeReturnTo;
    // Básica sin origen propio (o con origen "el padre") → su recorrido.
    if (basicActionRouteId)
      return `/surveillance-route/${basicActionRouteId}/child-orders`;
    if (safeReturnTo) return safeReturnTo;
    return navigationOrigin === 'workorder' && parentOrderId
      ? isCampaign
        ? `/work-orders/campaign/${parentOrderId}`
        : `/work-orders/${parentOrderId}`
      : '/work-orders/actions-validation';
  };

  const handleContinueAction = () => {
    console.log('🔘 [ActionSummary] handleContinueAction CALLED:', {
      isActionValidation,
      navigationOrigin,
      parentOrderId,
      sessionDataSnapshot: sessionData,
    });
    logRedirect('🔘 handleContinueAction called:', {
      isActionValidation,
      navigationOrigin,
      parentOrderId,
    });

    if (isActionValidation) {
      const exitRoute = getExitRoute();
      logRedirect('➡️ handleContinueAction: exit to', exitRoute);
      navigate(exitRoute);
    } else {
      dispatch(roidOTSelectedReset());
      removeNewActionStorage();
      // Context-aware redirect: Return to parent order if available
      // Use campaign route if parent is a campaign
      const targetRoute =
        srExitRoute ??
        (parentOrderId
          ? (isCampaign ? `/work-orders/campaign/${parentOrderId}` : `/work-orders/${parentOrderId}`)
          : '/work-orders');
      console.log('🎯 [ActionSummary] handleContinueAction: Redirect decision:', {
        hasParentOrderId: !!parentOrderId,
        parentOrderId,
        isCampaign,
        willRedirectTo: targetRoute,
      });
      if (parentOrderId) {
        console.log('➡️ [ActionSummary] handleContinueAction: Navigating to parent order:', targetRoute);
        logRedirect('➡️ Navigating to parent order:', targetRoute);
        navigate(targetRoute);
      } else {
        console.log('➡️ [ActionSummary] handleContinueAction: Navigating to work-orders list (no parentOrderId)');
        logRedirect('➡️ Navigating to work-orders list (no parentOrderId)');
        navigate('/work-orders');
      }
    }
  };

  // T99: Modal handlers removed - modal is now shown in ShowOT/ActionsValidation
  // after redirect, not in ActionSummary

  /**
   * Chip de estado del resumen. Era la ÚNICA de las tres puertas al modal sin
   * portero: se pasaba el manejador sin mirar el rol, así que cualquiera lo
   * pulsaba. Al Tablet le abría el modal vacío; al resto lo salvaba el
   * backend, que rechaza. Ahora comparte gate con las otras dos.
   */
  const canChangeState = canRoleChangeStateOf(
    currentRole as Role,
    OrderType.Action,
    loaderData?.state as OrderState
  );

  const handleStatusClick = () => {
    if (loaderData?.state && loaderData?.id) {
      dispatch(
        openModal(modalIds.CHANGE_STATE_MODAL, {
          orderType: OrderType.Action,
          currentState: loaderData.state as OrderState,
          orderId: loaderData.orderId,
          indexExpedition: loaderData.indexExpedition,
          isExternal: loaderData.isExternal,
          revalidatorFunction: () => {
            client.refetchQueries({
              include: [GET_ORDER_BY_ID],
            });
            revalidator.revalidate();
          },
          onClickClose: () => {
            dispatch(closeModal());
          },
        })
      );
    }
  };

  const handleValidateClick = () => {
    setShowConfirmModal(true);
  };

  const handleConfirmValidation = () => withNavigationFeedback(async () => {
    setIsValidating(true);
    setShowConfirmModal(false);

    // Store orderId for use in finally block (parent order check)
    const orderIdForCheck = loaderData.orderId;
    let validationSucceeded = false;
    // Con errores en los formularios el aviso se QUEDA en pantalla: ni se
    // comprueba el padre ni se sale al origen. Antes el `finally` navegaba
    // igual y el aviso desaparecía en el acto (bug 12-oct).
    let blockedByFormErrors = false;

    try {
      // 0. VALIDATE ALL FORMS FIRST (Sprint 2 Revisado)
      const validationResult = await validateAllForms();

      if (!validationResult.valid) {
        // Show validation errors modal instead of proceeding
        setValidationErrors(validationResult.errors);
        setShowValidationErrorsModal(true);
        setIsValidating(false);
        blockedByFormErrors = true;
        return; // Stop here - don't proceed with validation
      }

      // 1. Validate the Action via SAVE_ACTION (C-11.1 — replaces UPDATE_ACTION).
      // SAVE_ACTION devuelve { order, action, allActionsValidated, parentOrderId }
      // a nivel del result (no anidado dentro de action como UPDATE_ACTION).
      const { data: saveActionData } = await client.mutate({
        mutation: SAVE_ACTION,
        variables: {
          input: {
            id: loaderData.id,
            isValidate: true,
            state: OrderState.Validated,
          },
        },
        update(cache, { data: { saveAction } }) {
          // En update path el helper hace skip (Apollo escribe por id), pero
          // si por alguna razón este SAVE_ACTION fuese create, el helper
          // mantendría coherencia. Llamada idempotente.
          prependActionToCache(cache, saveAction);
          // Invalida informe Gestión Horaria + listas (horas/estado derivados no
          // se reescriben por id) → refresca al volver sin pulsar "Actualizar".
          evictDerivedCaches(cache);
        },
      });

      // T99: Log response for debugging
      logAutoClose('🔍 [T99] SAVE_ACTION response:', {
        allActionsValidated: saveActionData?.saveAction?.allActionsValidated,
        parentOrderId: saveActionData?.saveAction?.parentOrderId,
      });

      // Order close confirmation: Check if all actions are validated
      // IMPORTANT: Only trigger if this action CHANGED from non-validated to validated
      // This prevents the modal from appearing again when editing already-validated actions
      const previousState = loaderData.state;
      const actionChangedToValidated = previousState !== OrderState.Validated;

      // T99: Debug logs for close order confirmation
      console.log('🔍 [T99-ActionSummary] Checking close order conditions:', {
        allActionsValidated: saveActionData?.saveAction?.allActionsValidated,
        parentOrderId: saveActionData?.saveAction?.parentOrderId,
        previousState,
        actionChangedToValidated,
      });

      if (
        saveActionData?.saveAction?.allActionsValidated &&
        saveActionData?.saveAction?.parentOrderId &&
        actionChangedToValidated
      ) {
        logAutoClose('🎯 All actions validated! Dispatching to Redux for modal');
        console.log('🎯 [T99-ActionSummary] All actions validated! Will dispatch to Redux');

        // Dispatch to Redux - ShowOT/ActionsValidation will read and show modal
        // The reducer checks if user already declined this order

        // Query parent order to get its ROID for display in the modal
        let parentRoid: string | undefined;
        try {
          console.log('🔍 [T99-ActionSummary] Querying parent order for ROID:', saveActionData.saveAction.parentOrderId);
          const { data: parentOrderData } = await client.query({
            query: GET_ORDER_BY_ID,
            variables: { id: saveActionData.saveAction.parentOrderId },
            fetchPolicy: 'cache-first',
          });
          console.log('📋 [T99-ActionSummary] Parent order data:', parentOrderData?.order);
          parentRoid = Array.isArray(parentOrderData?.order?.roid)
            ? parentOrderData.order.roid.join('.')
            : parentOrderData?.order?.roid;
          console.log('📋 [T99-ActionSummary] Extracted ROID:', parentRoid);
          logAutoClose('📋 Parent order ROID:', parentRoid);
        } catch (err) {
          console.error('❌ [T99-ActionSummary] Error fetching parent order ROID:', err);
        }

        console.log('💾 [T99-ActionSummary] Dispatching orderCloseSetPending:', {
          parentOrderId: saveActionData.saveAction.parentOrderId,
          parentRoid,
        });
        // T99: Remove from declined list first (in case user said "No" before but now validated a NEW action)
        // This ensures the modal appears again when a new action is validated
        dispatch(orderCloseRemoveDeclined(saveActionData.saveAction.parentOrderId));
        dispatch(orderCloseSetPending(saveActionData.saveAction.parentOrderId, parentRoid));
        logAutoClose('💾 Dispatched orderCloseSetPending to Redux');
        console.log('✅ [T99-ActionSummary] Dispatch completed');
      } else {
        console.log('ℹ️ [T99-ActionSummary] Conditions NOT met for close order modal:', {
          allActionsValidated: saveActionData?.saveAction?.allActionsValidated,
          parentOrderId: saveActionData?.saveAction?.parentOrderId,
          actionChangedToValidated,
          reason: !saveActionData?.saveAction?.allActionsValidated
            ? 'Not all actions validated'
            : !saveActionData?.saveAction?.parentOrderId
              ? 'No parent order ID'
              : 'Action was already validated (no state change)',
        });
      }

      // The backend's actionsService.update() already syncs the Order to
      // 'validated' state (reverse sync) when isValidate=true. No need to
      // call UPDATE_ORDER separately — doing so was redundant and created
      // desync risk if this second mutation failed.

      validationSucceeded = true;
      // Redirect logic moved to finally block to ensure it executes even on errors
    } catch (error) {
      console.error('Error validating action:', error);
    } finally {
      setIsValidating(false);

      if (!blockedByFormErrors) {
        // Order close confirmation modal is shown AFTER redirect via Redux
        // ShowOT/ActionsValidation reads from Redux and shows the modal

        // 4. Check if parent order was auto-closed and show feedback
        // This runs in finally to ensure it executes even if there are errors
        // The backend auto-closes the parent order independently
        if (orderIdForCheck) {
          try {
            logAutoClose('Starting parent order check for orderId:', orderIdForCheck);

            // First, get the current order to retrieve parentExpeditionOrderIDs
            const { data: currentOrderData } = await client.query({
              query: GET_ORDER_BY_ID,
              variables: { id: orderIdForCheck },
              fetchPolicy: 'network-only',
            });

            logAutoClose('Current order data:', {
              orderId: orderIdForCheck,
              orderType: currentOrderData?.order?.type,
              orderState: currentOrderData?.order?.state,
              parentExpeditionOrderIDs: currentOrderData?.order?.parentExpeditionOrderIDs,
            });

            const parentExpeditionOrderId =
              currentOrderData?.order?.parentExpeditionOrderIDs?.[0];

            logAutoClose('Parent order ID extracted:', parentExpeditionOrderId);

            if (parentExpeditionOrderId) {
              // Query the parent order to check its state
              const { data: parentOrderData } = await client.query({
                query: GET_ORDER_BY_ID,
                variables: { id: parentExpeditionOrderId },
                fetchPolicy: 'network-only',
              });

              logAutoClose('Parent order data:', {
                parentExpeditionOrderId,
                parentState: parentOrderData?.order?.state,
                expectedState: OrderState.End,
                statesMatch: parentOrderData?.order?.state === OrderState.End,
              });

              if (parentOrderData?.order?.state === OrderState.End) {
                logAutoClose('✅ Parent order is in End state - showing toast');
                showSuccessToast(t('validation:parentOrderClosed'));
              } else {
                logAutoClose('❌ Parent order NOT in End state - no toast shown');
              }
            } else {
              logAutoClose('⚠️ No parent order ID found - skipping toast check');
            }
          } catch (error) {
            logAutoClose('❌ Error checking parent order state:', error);
            console.error('Error checking parent order state:', error);
          }
        }

        // 5. Retorno determinista al origen (returnTo). Ver spec 2026-06-05.
        const exitRoute = getExitRoute();
        logRedirect('🔘 handleConfirmValidation - exit to', exitRoute);
        navigate(exitRoute);
      }
    }
  });

  const handleCancelValidation = () => {
    setShowConfirmModal(false);
  };

  // Tarea #7 — Pendent de certificar: paralelo a la validación pero con
  // state: pending-certification. Se reutiliza el mismo flujo T99 de modal de
  // cierre de OT padre (el backend acepta validated + pending-certification
  // como estados "listos para cerrar" en checkAllActionsValidated).
  const handlePendingCertificationClick = () => {
    setShowPendingCertificationModal(true);
  };

  const handleCancelPendingCertification = () => {
    setShowPendingCertificationModal(false);
  };

  const handleConfirmPendingCertification = () => withNavigationFeedback(async () => {
    setIsMarkingPendingCertification(true);
    setShowPendingCertificationModal(false);

    const orderIdForCheck = loaderData.orderId;
    // Igual que en «Validar»: con errores el aviso se queda y no se sale.
    let blockedByFormErrors = false;

    try {
      const validationResult = await validateAllForms();
      if (!validationResult.valid) {
        setValidationErrors(validationResult.errors);
        setShowValidationErrorsModal(true);
        setIsMarkingPendingCertification(false);
        blockedByFormErrors = true;
        return;
      }

      // C-11.1: SAVE_ACTION en lugar de UPDATE_ACTION (path homogéneo).
      const { data: saveActionData } = await client.mutate({
        mutation: SAVE_ACTION,
        variables: {
          input: {
            id: loaderData.id,
            isValidate: true,
            state: OrderState.PendingCertification,
          },
        },
        update(cache, { data: { saveAction } }) {
          prependActionToCache(cache, saveAction);
          evictDerivedCaches(cache);
        },
      });

      const previousState = loaderData.state;
      const actionChangedToPendingCertification =
        previousState !== OrderState.PendingCertification;

      if (
        saveActionData?.saveAction?.allActionsValidated &&
        saveActionData?.saveAction?.parentOrderId &&
        actionChangedToPendingCertification
      ) {
        let parentRoid: string | undefined;
        try {
          const { data: parentOrderData } = await client.query({
            query: GET_ORDER_BY_ID,
            variables: { id: saveActionData.saveAction.parentOrderId },
            fetchPolicy: 'cache-first',
          });
          parentRoid = Array.isArray(parentOrderData?.order?.roid)
            ? parentOrderData.order.roid.join('.')
            : parentOrderData?.order?.roid;
        } catch (err) {
          console.error(
            '❌ [T99-ActionSummary] Error fetching parent order ROID (pending-certification):',
            err
          );
        }

        dispatch(
          orderCloseRemoveDeclined(saveActionData.saveAction.parentOrderId)
        );
        dispatch(
          orderCloseSetPending(
            saveActionData.saveAction.parentOrderId,
            parentRoid
          )
        );
      }
    } catch (error) {
      console.error('Error marking action as pending-certification:', error);
    } finally {
      setIsMarkingPendingCertification(false);

      if (!blockedByFormErrors) {
        if (orderIdForCheck) {
          try {
            const { data: currentOrderData } = await client.query({
              query: GET_ORDER_BY_ID,
              variables: { id: orderIdForCheck },
              fetchPolicy: 'network-only',
            });
            const parentExpeditionOrderId =
              currentOrderData?.order?.parentExpeditionOrderIDs?.[0];

            if (parentExpeditionOrderId) {
              const { data: parentOrderData } = await client.query({
                query: GET_ORDER_BY_ID,
                variables: { id: parentExpeditionOrderId },
                fetchPolicy: 'network-only',
              });
              if (parentOrderData?.order?.state === OrderState.End) {
                showSuccessToast(t('validation:parentOrderClosed'));
              }
            }
          } catch (err) {
            console.error('Error checking parent order state:', err);
          }
        }

        navigate(getExitRoute());
      }
    }
  });

  const handleNavigateToTab = (tab: string) => {
    setShowValidationErrorsModal(false);
    navigate(`../${loaderData.id}/${tab}`, {
      state: { validateOnMount: true },
    });
  };

  const totalHours = `${Math.floor(loaderData.minutesTotals / 60)
    .toString()
    .padStart(2, '0')}:${(loaderData.minutesTotals % 60)
      .toString()
      .padStart(2, '0')}`;

  console.log(
    '🔍 ActionSummaryPage DATA:',
    JSON.stringify({
      operators: loaderData.operators,
      vehicles: loaderData.vehicles,
      externalOperatorsCount: loaderData.externalOperatorsCount,
      isExternal: loaderData.isExternal,
      orderId: loaderData.orderId,
      state: loaderData.state,
      canValidate,
      currentRole,
    })
  );

  return (
    <>
      {loaderData && (
        <ActionContentCardResume
          works={loaderData.works}
          routes={routes}
          roid={loaderData.roid}
          actionStatus={loaderData.state}
          date={loaderData.date ? format(new Date(loaderData.date), 'dd/MM/yy') : '-'}
          hour={loaderData.date ? format(new Date(loaderData.date), 'HH:mm') : '-'}
          capitol={loaderData.capitol}
          subCapitol={loaderData.subCapitol}
          concession={loaderData.concession}
          roadDirections={loaderData.roadDirections}
          direction={loaderData.direction}
          margin={loaderData.margin}
          road={loaderData.road}
          pkInit={loaderData.pkInit}
          pkEnd={loaderData.pkEnd}
          track={loaderData.track}
          city={loaderData.city}
          lane={loaderData.lane}
          element={loaderData.elementOrder}
          laneCutting={loaderData.action.laneCutting}
          sketch={loaderData.sketch}
          images={loaderData.images}
          description={loaderData.description}
          descriptionValidated={loaderData.descriptionValidated}
          operatorDescription={loaderData.operatorDescription}
          creatorName={loaderData.creatorName}
          registeredByName={loaderData.registeredByName}
          notes={loaderData.notes}
          leftButton={
            // RoomTeam solo vista; Tablet solo vista si el padre NO está en un
            // estado de TRABAJO; estados terminales.
            //
            // 2026-08-05: se compara contra TABLET_CONTENT_CREATION_STATES —
            // la MISMA constante que decide el botón de crear — en vez de
            // contra WorkInProgress a pelo. Para un padre genérico el
            // resultado es idéntico (la constante lo contiene); lo que gana
            // son `in-progress` y `paused`, que solo existen en el
            // Recorregut de Vigilància y donde el Tablet SÍ trabaja: el padre
            // de una actuación básica es el recorrido, que NUNCA está en
            // `work-in-progress`, así que aquí se quedaba sin «Editar» y con
            // «Volver» donde debía decir «Guardar».
            //
            // Con `parentOrderState === null` (consulta en vuelo o fallida)
            // `includes` da false y sigue bloqueando, igual que antes.
            currentRole === Role.RoomTeam ||
            (currentRole === Role.Tablet &&
              !TABLET_CONTENT_CREATION_STATES.includes(
                parentOrderState as OrderState
              )) ||
            isParentBlocked ||
            editBlockedForRole ||
            loaderData.state === OrderState.Annulled ||
            loaderData.state === OrderState.End
              ? undefined
              : (!isActionEditedBoolean && isActionValidation
                ? t('buttons:continue')
                : t('buttons:edit'))
          }
          rightButton={
            !isActionEditedBoolean && isActionValidation
              ? t('buttons:back')
              : ((currentRole === Role.Tablet &&
                   !TABLET_CONTENT_CREATION_STATES.includes(
                     parentOrderState as OrderState
                   )) ||
                 loaderData.state === OrderState.Annulled ||
                 loaderData.state === OrderState.End
                  ? t('buttons:back')
                  : t('buttons:save'))
          }
          isActionEditedBoolean={isActionEditedBoolean}
          onEditAction={handleEditAction}
          onContinueAction={handleContinueAction}
          asset={loaderData.assetValue}
          assetSwitch={loaderData.assetSwitch}
          activity={loaderData.activityValue}
          mapCenter={loaderData.centerMap}
          mapPoint={loaderData.pointMap}
          path={loaderData?.path ?? []}
          geolocation={loaderData.geolocation}
          // Actuació bàsica: operarios, vehículos y maquinaria son del
          // recorrido, no suyos — el resumen de la validación es espejo de su
          // wizard de creación, que tampoco los pide (FR-9.3).
          //
          // 🔴 Aquí manda `hideCrewInSummary` (la básica, venga de donde
          // venga) y NO `skipOperatorsChecks`, que está condicionado a estar
          // en la validación: fuera de ella el resumen solo se callaba porque
          // las listas venían vacías, así que una básica antigua con
          // tripulación heredada la enseñaba como suya. Mismo criterio que el
          // informe PDF (2026-09-04). `skipOperatorsChecks` sigue gobernando
          // la navegación y la pre-validación, que sí dependen de la pantalla.
          operators={hideCrewInSummary ? [] : loaderData.operators}
          vehicles={hideCrewInSummary ? [] : loaderData.vehicles}
          isMaterialTableDisplayed={!!loaderData.materialsValue}
          materialsData={loaderData.materialsValue ?? []}
          isAnimalsTableDisplayed={!skipAnimals && !!loaderData.animals}
          animalsData={skipAnimals ? [] : (loaderData.animals ?? [])}
          isMachineryTableDisplayed={
            !hideCrewInSummary && !!loaderData.machineryValue
          }
          machineryData={
            hideCrewInSummary ? [] : (loaderData.machineryValue ?? [])
          }
          isBudgetItemsTableDisplayed={
            !!loaderData.budgetItemsValue && roleCanViewBudgets(currentRole)
          }
          budgetItemsData={loaderData.budgetItemsValue ?? []}
          startDate={loaderData.registerInitDate ? format(
            new Date(loaderData.registerInitDate),
            'dd/MM/yyyy'
          ) : '-'}
          startHour={loaderData.registerInitDate ? format(new Date(loaderData.registerInitDate), 'HH:mm') : '-'}
          endDate={loaderData.registerEndDate ? format(new Date(loaderData.registerEndDate), 'dd/MM/yyyy') : '-'}
          endHour={loaderData.registerEndDate ? format(new Date(loaderData.registerEndDate), 'HH:mm') : '-'}
          // Fechas ISO completas → jornada cross-day correcta en el resumen
          // (mismo cálculo que el wizard). Sin ellas el resumen ignoraba el día.
          registerInitDate={loaderData.registerInitDate ?? undefined}
          registerEndDate={loaderData.registerEndDate ?? undefined}
          // La básica no tiene la pestaña de operarios: su horario va aparte.
          basicSchedule={hideCrewInSummary}
          arrivalDate={loaderData.registerArrivalDate ? format(new Date(loaderData.registerArrivalDate), 'dd/MM/yyyy') : undefined}
          arrivalHour={loaderData.registerArrivalDate ? format(new Date(loaderData.registerArrivalDate), 'HH:mm') : undefined}
          totalHours={totalHours}
          totalOperators={
            skipOperatorsChecks
              ? '0'
              : `${loaderData.operators.length + (loaderData.externalOperatorsCount || 0)
                }`
          }
          isExternal={loaderData.isExternal}
          isSimultaneous={loaderData.isSimultaneous}
          // Validate button props — only on first summary (entry), not after flow
          validateButton={t('validation:validate')}
          showValidateButton={canValidate && !isActionEditedBoolean}
          isValidating={isValidating}
          onValidateAction={handleValidateClick}
          // Tarea #7 — Pendent de certificar (paralelo al botón de validar)
          pendingCertificationButton={t('validation:pendingCertification')}
          showPendingCertificationButton={
            canMarkPendingCertification && !isActionEditedBoolean
          }
          isMarkingPendingCertification={isMarkingPendingCertification}
          onPendingCertificationAction={handlePendingCertificationClick}
          onStatusClick={canChangeState ? handleStatusClick : undefined}
        />
      )}

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <ModalOverlay onClick={handleCancelValidation}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <Typography size="L" variant="semiBold">
              {t('validation:confirmValidation.title', 'Confirmar validació')}
            </Typography>
            <Typography size="M" style={{ marginTop: '1rem' }}>
              {t(
                'validation:confirmValidation.message',
                "Estàs segur que vols validar aquesta actuació?"
              )}
            </Typography>
            <ModalButtons>
              <MainButton
                variant="secondary"
                text={t('buttons:cancel', 'Cancel·lar')}
                onClick={handleCancelValidation}
                disabled={isValidating}
              />
              <MainButton
                text={
                  isValidating
                    ? t('buttons:validating')
                    : t('buttons:confirm', 'Confirmar')
                }
                onClick={handleConfirmValidation}
                disabled={isValidating}
              />
            </ModalButtons>
          </ModalContent>
        </ModalOverlay>
      )}

      {/* Tarea #7 — Confirmation Modal for Pendent de certificar */}
      {showPendingCertificationModal && (
        <ModalOverlay onClick={handleCancelPendingCertification}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <Typography size="L" variant="semiBold">
              {t('validation:confirmPendingCertification.title')}
            </Typography>
            <Typography size="M" style={{ marginTop: '1rem' }}>
              {t('validation:confirmPendingCertification.message')}
            </Typography>
            <ModalButtons>
              <MainButton
                variant="secondary"
                text={t('buttons:cancel', 'Cancel·lar')}
                onClick={handleCancelPendingCertification}
                disabled={isMarkingPendingCertification}
              />
              <MainButton
                text={
                  isMarkingPendingCertification
                    ? t('buttons:markingCertification', 'Marcant...')
                    : t('buttons:confirm', 'Confirmar')
                }
                onClick={handleConfirmPendingCertification}
                disabled={isMarkingPendingCertification}
              />
            </ModalButtons>
          </ModalContent>
        </ModalOverlay>
      )}

      {/* Validation Errors Modal */}
      {showValidationErrorsModal && (
        <ModalOverlay onClick={() => setShowValidationErrorsModal(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <Typography size="L" variant="semiBold" style={{ color: '#d32f2f' }}>
              {t('validation:errors.title', 'Errors de validació')}
            </Typography>
            <Typography size="M" style={{ marginTop: '0.5rem' }}>
              {t('validation:errors.message', 'Si us plau, corregeix els següents errors abans de validar:')}
            </Typography>
            <ErrorList>
              {Object.entries(
                validationErrors.reduce((acc, error) => {
                  if (!acc[error.tab]) acc[error.tab] = [];
                  acc[error.tab].push(error);
                  return acc;
                }, {} as Record<string, typeof validationErrors>)
              ).map(([tab, errors]) => (
                <ErrorGroup key={tab}>
                  <TabName onClick={() => handleNavigateToTab(tab)}>
                    {errors[0].tabLabel} ›
                  </TabName>
                  {errors.map((error, idx) => (
                    <ErrorItem key={idx}>{error.message}</ErrorItem>
                  ))}
                </ErrorGroup>
              ))}
            </ErrorList>
            <ModalButtons>
              <MainButton
                text={t('buttons:close', 'Tancar')}
                onClick={() => setShowValidationErrorsModal(false)}
              />
            </ModalButtons>
          </ModalContent>
        </ModalOverlay>
      )}

      {/* Order Close Confirmation Modal se muestra en ShowOT/ActionsValidation
          después del redirect. Aquí solo hacemos dispatch a Redux. */}
    </>
  );
}
